import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash.get';
import Global from '../../store/global-selectors';
import FilterBar from '../../components/filter-bar';
import { criteriaOperatorsMap } from '../../containers/base-filter-bar-container/criteria-operators';
import { returnsChangeFilters } from '../../store/returns/filtering/returns.filtering.actions';
import { otherFilterByOptions, otherFilterByOptionsReceivedView, popularFilterByOptions } from './filter-options/';
import { getCriterionArguments } from './criteria-arguments';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

import style from './style.module.less';
import { useSortedTranslatedCountries } from '../../hooks/use-sorted-translated-countries';
import { FilterOptionPropType } from '../../prop-types/filter-option';
import { AppliedFilterPropType } from '../../prop-types/applied-filter';

const allOptions = [...popularFilterByOptions, ...otherFilterByOptions];

const mapStateToProps = (state) => {
  const statusViewId = Global.selectReturnsNavSelectedViewId(state);
  const otherFilterBys = statusViewId === 'received' ? otherFilterByOptionsReceivedView : otherFilterByOptions;
  const criteriaArgumentsMap = getCriterionArguments(state);
  // filters out international services as we do not yet support international returns.
  if (criteriaArgumentsMap.requestedService) {
    const allServices = Global.selectSellerServices(state);
    criteriaArgumentsMap.requestedService = criteriaArgumentsMap.requestedService.filter((service) => {
      const matchingService = allServices.find((s) => s.serviceId === service.value);
      return !get(matchingService, 'features.supportsInternationalShipping');
    });
  }
  const appliedFilters = Global.selectReturnsFilteringAppliedFiltersByViewId(state, { statusViewId });

  return {
    allFilterBys: allOptions,
    popularFilterBys: popularFilterByOptions,
    otherFilterBys,
    criteriaArgumentsMap,
    criteriaOperatorsMap,
    statusViewId,
    appliedFilters,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onFiltersChanged: (statusViewId, appliedFilters) => dispatch(returnsChangeFilters(statusViewId, appliedFilters)),
  };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { statusViewId } = stateProps;
  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    onFiltersChanged: (appliedFilters) => dispatchProps.onFiltersChanged(statusViewId, appliedFilters),
  };
};

const FilterBarContainer = ({
  onFiltersChanged,
  criteriaArgumentsMap = emptyObject,
  appliedFilters = emptyArray,
  allFilterBys,
  popularFilterBys,
  otherFilterBys,
  criteriaOperatorsMap: propCriteriaOperatorsMap,
}) => {
  const countries = useSortedTranslatedCountries({ labelProp: 'displayName' });
  const correctedCriteriaArgumentsMap = useMemo(() => {
    const correctedCounties = countries.map((country) => ({
      ...country,
      id: 'option-country',
      value: country.code,
    }));
    return {
      ...criteriaArgumentsMap,
      country: correctedCounties,
      returnsCountry: correctedCounties,
    };
  }, [criteriaArgumentsMap, countries]);

  const clearFilters = useCallback(() => {
    onFiltersChanged(emptyArray);
  }, [onFiltersChanged]);

  return (
    <div className={style['filter-bar-wrapper']}>
      <FilterBar
        allFilterBys={allFilterBys}
        appliedFilters={appliedFilters}
        clearFilters={clearFilters}
        criteriaArgumentsMap={correctedCriteriaArgumentsMap}
        criteriaOperatorsMap={propCriteriaOperatorsMap}
        onFiltersChanged={onFiltersChanged}
        otherFilterBys={otherFilterBys}
        popularFilterBys={popularFilterBys}
      />
    </div>
  );
};

FilterBarContainer.propTypes = {
  appliedFilters: PropTypes.arrayOf(AppliedFilterPropType),
  onFiltersChanged: PropTypes.func.isRequired,
  criteriaArgumentsMap: PropTypes.object,
  allFilterBy: PropTypes.arrayOf(FilterOptionPropType),
  popularFilterBys: PropTypes.arrayOf(FilterOptionPropType),
  otherFilterBys: PropTypes.arrayOf(FilterOptionPropType),
  criteriaOperatorsMap: PropTypes.object,
};

export const ReturnsFilterBarContainer = connect(mapStateToProps, mapDispatchToProps, mergeProps)(FilterBarContainer);
